import { XMarkIcon } from '@heroicons/react/24/solid';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
} from '@heroui/react';
import _ from 'lodash';
import { createContext, type FC, useCallback, useContext, useMemo, useState } from 'react';
import { Badge } from '@/primitives/badge.tsx';
import {
  ResponsiveTable,
  TableTile,
  TableTileField,
  TableTileList,
} from '@/primitives/responsive-table.tsx';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useSort,
} from '@/primitives/table.tsx';
import { TextLink } from '@/primitives/text.tsx';
import type { AccountRepresentativeListModel } from '../../types/account-representative.type.ts';
import { EventStatus, eventBadgeColor } from '../../types/constants.ts';
import {
  type RepresentativeSearchModel,
  representativeHasValidSubscription,
} from '../../types/representative.type.ts';
import { RepresentativeStatusEnum } from '../../types/representative-status.type.ts';
import { NoData } from '../no-data.tsx';
import RepFavorite from './rep-favorite.tsx';
import RepRating from './rep-rating.tsx';
import RepTableActions from './rep-table-actions.tsx';
import RepresentativeEditDialog from './representative-edit-dialog.tsx';

interface RepresentativeTableProps {
  data: RepresentativeSearchModel[];
}

type EditAccountRepContext = {
  openEdit: (value: AccountRepresentativeListModel) => void;
  open: boolean;
  accountRep: AccountRepresentativeListModel;
};

const EditAccountRepContext = createContext<EditAccountRepContext | undefined>(undefined);

export const useEditAccountRepContext = () => {
  const context = useContext(EditAccountRepContext);
  if (!context) {
    throw new Error('Must be used within EditAccountRepContext');
  }

  return context;
};

function RepStatusBadge({ accountRep }: { accountRep?: AccountRepresentativeListModel }) {
  const isApproved = accountRep?.representativeStatus?.id === RepresentativeStatusEnum.Approved;
  const isRejected = accountRep?.representativeStatus?.id === RepresentativeStatusEnum.Rejected;
  const isPending = !accountRep?.representativeStatus?.id;

  return (
    <>
      {isApproved && <Badge color={eventBadgeColor[EventStatus.Approved]}>Approved</Badge>}
      {isRejected && <Badge color={eventBadgeColor[EventStatus.Canceled]}>Rejected</Badge>}
      {isPending && <Badge color={eventBadgeColor[EventStatus.Requested]}>Pending</Badge>}
    </>
  );
}

function RepNameLink({
  value,
  showFavorite,
  accountRep,
}: {
  value: RepresentativeSearchModel;
  showFavorite?: boolean;
  accountRep?: AccountRepresentativeListModel;
}) {
  return (
    <div className='flex flex-row gap-2'>
      {showFavorite && <RepFavorite data={accountRep} />}
      <TextLink
        title='View profile'
        href={`/rep-public-profile/${value.representative.key}`}
        onClick={e => e.stopPropagation()}
      >
        {value.representative.firstName} {value.representative.lastName}
      </TextLink>
    </div>
  );
}

function RepActiveLabel({ value }: { value: RepresentativeSearchModel }) {
  return representativeHasValidSubscription(value.representative) ? 'Yes' : 'No';
}

const RepresentativeTable: FC<RepresentativeTableProps> = ({ data }) => {
  const [open, setOpen] = useState(false);
  const [accountRep, setAccountRep] = useState<AccountRepresentativeListModel | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRep, setSelectedRep] = useState<RepresentativeSearchModel | null>(null);

  const onOpenDialog = (value: AccountRepresentativeListModel) => {
    setAccountRep(value);
    setOpen(true);
  };

  const onOpenDrawer = (value: RepresentativeSearchModel) => {
    setSelectedRep(value);
    setDrawerOpen(true);
  };

  const onCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedRep(null);
  };

  const memoizedSortingDataAccessor = useCallback(
    (value: RepresentativeSearchModel, column: string) => {
      switch (column) {
        case 'category':
          return value.accountRepresentatives
            ?.map((x: AccountRepresentativeListModel) => x.accountCategory?.description)
            .join(', ');
        case 'products':
          return value.representative.drugs?.join(', ');
        case 'status':
          return value.accountRepresentatives
            ?.map(x => x.representativeStatus?.description)
            .join(', ');
        case 'active':
          return representativeHasValidSubscription(value.representative) ? 'Yes' : 'No';
        case 'rating':
          return value.accountRepresentatives
            ?.map(x => x.rating)
            .reduce((prev, curr) => prev + curr, 0);
        default:
          return _.get(value, column);
      }
    },
    [],
  );

  const { sort, setSort, sortData } = useSort({
    data,
    memoizedSortingDataAccessor,
  });

  const contextValue = useMemo(
    () => ({ open, openEdit: onOpenDialog, accountRep }),
    [open, accountRep],
  );

  const hasReps = Boolean(sortData?.length);

  return (
    <EditAccountRepContext.Provider value={contextValue}>
      <ResponsiveTable
        currentSort={sort}
        setCurrentSort={setSort}
        tiles={
          <TableTileList empty={!hasReps} emptyContent={<NoData />}>
            {hasReps &&
              sortData.map(value => (
                <RepTableTile
                  key={value.representative.id}
                  value={value}
                  onOpenDrawer={onOpenDrawer}
                />
              ))}
          </TableTileList>
        }
      >
        <TableHead>
          <TableRow>
            <TableHeader sortColumn='status'>Status</TableHeader>
            <TableHeader sortColumn='representative.lastName'>Name</TableHeader>
            <TableHeader sortColumn='products'>Products</TableHeader>
            <TableHeader sortColumn='representative.company'>Company</TableHeader>
            <TableHeader sortColumn='category'>Category</TableHeader>
            <TableHeader sortColumn='rating'>Rating</TableHeader>
            <TableHeader sortColumn='active'>Active</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {!hasReps && (
            <TableRow>
              <TableCell colSpan={7}>
                <NoData />
              </TableCell>
            </TableRow>
          )}
          {hasReps &&
            sortData.map(value => (
              <RepTableRowWrapper
                key={value.representative.id}
                value={value}
                onOpenDrawer={onOpenDrawer}
              />
            ))}
        </TableBody>
      </ResponsiveTable>
      <RepresentativeEditDialog
        isOpen={open}
        setIsOpen={setOpen}
        accountRepKey={accountRep?.key}
        accountId={accountRep?.accountId}
      />
      <Drawer
        hideCloseButton
        isOpen={drawerOpen}
        onClose={onCloseDrawer}
        placement='right'
        size='lg'
      >
        <DrawerContent>
          <DrawerHeader className='flex items-center justify-between border-b bg-gradient-primary text-primary-950'>
            <h2 className='text-xl font-semibold'>
              {selectedRep?.representative.firstName} {selectedRep?.representative.lastName}
            </h2>
            <Button isIconOnly variant='light' onPress={onCloseDrawer} color='default'>
              <XMarkIcon className='size-5' />
            </Button>
          </DrawerHeader>
          <DrawerBody className='p-6'>
            {selectedRep && (
              <div className='space-y-3'>
                {selectedRep.accountRepresentatives.map(accountRep => {
                  const isApproved =
                    accountRep.representativeStatus?.id === RepresentativeStatusEnum.Approved;
                  const isRejected =
                    accountRep.representativeStatus?.id === RepresentativeStatusEnum.Rejected;
                  const isPending = !accountRep.representativeStatus?.id;

                  return (
                    <Card key={accountRep.id} shadow='none' className='border border-gray-200'>
                      <CardBody className='p-4'>
                        <div className='flex items-start justify-between gap-3 mb-3'>
                          <div className='flex-1'>
                            <div className='flex items-center gap-2 mb-1'>
                              <RepFavorite data={accountRep} />
                              <h3 className='text-base font-semibold text-gray-900'>
                                {accountRep.accountName}
                              </h3>
                            </div>
                            {accountRep.accountCategory?.description && (
                              <p className='text-sm text-gray-600'>
                                {accountRep.accountCategory.description}
                              </p>
                            )}
                          </div>
                          <div>
                            {isApproved && (
                              <Chip size='sm' color='success' variant='flat'>
                                Approved
                              </Chip>
                            )}
                            {isRejected && (
                              <Chip size='sm' color='danger' variant='flat'>
                                Rejected
                              </Chip>
                            )}
                            {isPending && (
                              <Chip size='sm' color='default' variant='flat'>
                                Pending
                              </Chip>
                            )}
                          </div>
                        </div>

                        <div className='flex items-center justify-between pt-3 border-t border-gray-200'>
                          <div>
                            <RepRating data={accountRep} />
                          </div>
                          <RepTableActions data={accountRep} />
                        </div>
                      </CardBody>
                    </Card>
                  );
                })}
              </div>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </EditAccountRepContext.Provider>
  );
};

const RepTableRowWrapper = ({
  value,
  onOpenDrawer,
}: {
  value: RepresentativeSearchModel;
  onOpenDrawer: (value: RepresentativeSearchModel) => void;
}) => {
  const isMultiAccount = value.accountRepresentatives.length > 1;
  const accountRep = _.head(value.accountRepresentatives);
  const statusContent = isMultiAccount ? (
    'Multiple accounts'
  ) : (
    <RepStatusBadge accountRep={accountRep} />
  );
  const categoryContent = isMultiAccount
    ? 'Click to see categories'
    : accountRep?.accountCategory?.description;
  const ratingContent = isMultiAccount ? 'Click to see ratings' : <RepRating data={accountRep} />;

  return (
    <TableRow className='cursor-pointer hover:bg-gray-50' onClick={() => onOpenDrawer(value)}>
      <TableCell>{statusContent}</TableCell>
      <TableCell>
        <RepNameLink value={value} showFavorite={!isMultiAccount} accountRep={accountRep} />
      </TableCell>
      <TableCell>{value.representative.drugs.join(', ')}</TableCell>
      <TableCell>{value.representative.company}</TableCell>
      <TableCell>{categoryContent}</TableCell>
      <TableCell>{ratingContent}</TableCell>
      <TableCell>
        <RepActiveLabel value={value} />
      </TableCell>
    </TableRow>
  );
};

const RepTableTile = ({
  value,
  onOpenDrawer,
}: {
  value: RepresentativeSearchModel;
  onOpenDrawer: (value: RepresentativeSearchModel) => void;
}) => {
  const isMultiAccount = value.accountRepresentatives.length > 1;
  const accountRep = _.head(value.accountRepresentatives);
  const statusContent = isMultiAccount ? (
    'Multiple accounts'
  ) : (
    <RepStatusBadge accountRep={accountRep} />
  );
  const categoryContent = isMultiAccount
    ? 'Click to see categories'
    : accountRep?.accountCategory?.description;
  const ratingContent = isMultiAccount ? 'Click to see ratings' : <RepRating data={accountRep} />;

  return (
    <TableTile onClick={() => onOpenDrawer(value)}>
      <div className='flex flex-col gap-3'>
        <TableTileField label='Status'>{statusContent}</TableTileField>
        <TableTileField label='Name'>
          <RepNameLink value={value} showFavorite={!isMultiAccount} accountRep={accountRep} />
        </TableTileField>
        <TableTileField label='Products'>{value.representative.drugs.join(', ')}</TableTileField>
        <TableTileField label='Company'>{value.representative.company}</TableTileField>
        <TableTileField label='Category'>{categoryContent}</TableTileField>
        <TableTileField label='Rating'>{ratingContent}</TableTileField>
        <TableTileField label='Active'>
          <RepActiveLabel value={value} />
        </TableTileField>
      </div>
    </TableTile>
  );
};

export default RepresentativeTable;
